// Redux
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

// Constants
import { LoadingStates, UNAUTHORISED } from "constants/global";

// API
import {
	createRetroClaim as APICreateRetroClaim,
	approveRetroClaim as APIApproveRetroClaim,
	rejectRetroClaim as APIRejectRetroClaim,
	editRetroClaim as APIEditRetroClaim,
	approvalRequestRetroClaim as APIApprovalRequestRetroClaim,
	getRetroClaimList as APIGetRetroClaimList,
	getRetroClaimPermissions as APIGetRetroClaimPermissions
} from "services/api/claimManagement";

// Type Definitions
import {
	ApiErrorResponse,
	CreateRetroClaimResponse,
	RejectRetroClaimResponse,
	GetRetroClaimListResponse,
	RequestApprovalRetroClaimResponse,
	ApproveRetroClaimResponse,
	GetRetroClaimPermissionsResponse
} from "services/api/types";
import { ClaimManagement, CreateClaimType, RetroClaimType, RootState } from "redux/types";

export const initialState: ClaimManagement = {
	createClaim: {
		receipt_number: "",
		outlet_code: "",
		cr_card_number: "",
		receipt_datetime: "",
		purchase_amount: undefined,
		net_amount: undefined,
		source: "",
		payment_mode: "",
		receipt_img_base64: ""
	},

	// Actual Intital Redux State
	retroClaim: {
		receipt_number: "",
		outlet_code: "",
		cr_card_number: "",
		receipt_datetime: "",
		purchase_amount: undefined,
		net_amount: undefined,
		source: "",
		payment_mode: "",
		status: "",
		receipt_img_base64: "",
		submission_datetime: "",
		retro_claim_id: undefined
	},
	specialApproval: {
		loading: LoadingStates.DEFAULT
	},
	loading: LoadingStates.DEFAULT,
	claimResponse: {},
	claimList: {
		loading: LoadingStates.DEFAULT
	},
	claimPermissions: null,
	hasUnaurizedError: false
};

const setAuthTokenError = (draft: ClaimManagement, error: any): void => {
	if (error?.return_description === UNAUTHORISED) {
		draft.hasUnaurizedError = true;
	}
};

export const createRetroClaim = createAsyncThunk<CreateRetroClaimResponse, CreateClaimType, { rejectValue: ApiErrorResponse; state: RootState }>(
	"CREATE_NEW_RETRO_CLAIM",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APICreateRetroClaim(payload);

			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const approveRetroClaim = createAsyncThunk<ApproveRetroClaimResponse, { retro_claim_id: number }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"APPROVE_RETRO_CLAIM",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIApproveRetroClaim(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const rejectRetroClaim = createAsyncThunk<RejectRetroClaimResponse, { retro_claim_id: number; remarks: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"REJECT_RETRO_CLAIM",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIRejectRetroClaim(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const editRetroClaim = createAsyncThunk<CreateRetroClaimResponse, RetroClaimType, { rejectValue: ApiErrorResponse; state: RootState }>(
	"EDIT_RETRO_CLAIM",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIEditRetroClaim(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const getRetroClaimList = createAsyncThunk<
	GetRetroClaimListResponse,
	{
		from_receipt_date: string;
		to_receipt_date: string;
		page_number?: number;
		page_records?: number;
		from_net_amount?: number;
		to_net_amount?: number;
		perm_id?: number;
		claim_status?: Array<string>;
	},
	{ rejectValue: ApiErrorResponse; state: RootState }
>("GET_RETRO_CLAIM_LIST", async (payload, { rejectWithValue }) => {
	try {
		const modifiedPayload = { ...payload };
		delete modifiedPayload.perm_id;
		const { data } = await APIGetRetroClaimList(modifiedPayload);
		return { ...data, selectedAmountLimit: parseInt(`${payload.perm_id}${payload.to_net_amount}`) };
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

export const specialApprovalRetroClaim = createAsyncThunk<RequestApprovalRetroClaimResponse, FormData, { rejectValue: ApiErrorResponse; state: RootState }>(
	"SPECIAL_REQUEST_RETRO_CLAIM",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIApprovalRequestRetroClaim(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);
export const getRetroClaimPermissions = createAsyncThunk<GetRetroClaimPermissionsResponse, { retro_claim_id: number }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_RETRO_CLAIM_PERMISSIONS",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIGetRetroClaimPermissions(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

// Slice
export const retroClaimSlice = createSlice({
	name: "RETRO_CLAIM",
	initialState,
	reducers: {
		resetClaimState: () => {
			return { ...initialState };
		},
		createInfoUpdater: (state: ClaimManagement, action: PayloadAction<any>) => {
			state.createClaim = { ...state.createClaim, ...action.payload };
		},
		retroClaimInfoUpdater: (state: ClaimManagement, action: PayloadAction<any>) => {
			state.retroClaim = { ...state.retroClaim, ...action.payload };
		}
	},

	extraReducers: (builder) => {
		// Create Retro Claim
		builder.addCase(createRetroClaim.pending, (draft: ClaimManagement) => {
			draft.loading = LoadingStates.LOADING;
		});

		builder.addCase(createRetroClaim.fulfilled, (draft: ClaimManagement, { payload }) => {
			draft.claimResponse = payload;

			draft.loading = LoadingStates.IDLE;
		});
		builder.addCase(createRetroClaim.rejected, (state, { payload }) => {
			state.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});
		// Get Retro claim list
		builder.addCase(getRetroClaimList.pending, (draft: ClaimManagement) => {
			draft.claimList.loading = LoadingStates.LOADING;
		});

		builder.addCase(getRetroClaimList.fulfilled, (draft: ClaimManagement, { payload }) => {
			draft.claimList[payload?.selectedAmountLimit] = payload.data;
			draft.claimList.loading = LoadingStates.IDLE;
		});

		builder.addCase(getRetroClaimList.rejected, (state, { payload }) => {
			state.claimList.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// Edit Retro Claim
		builder.addCase(editRetroClaim.pending, (draft: ClaimManagement) => {
			draft.loading = LoadingStates.LOADING;
		});

		builder.addCase(editRetroClaim.fulfilled, (draft: ClaimManagement, { payload }) => {
			draft.claimResponse = payload;
			draft.loading = LoadingStates.IDLE;
		});
		builder.addCase(editRetroClaim.rejected, (state, { payload }) => {
			state.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// Reject Retro Claim
		builder.addCase(rejectRetroClaim.pending, (draft: ClaimManagement) => {
			draft.loading = LoadingStates.LOADING;
		});

		builder.addCase(rejectRetroClaim.fulfilled, (draft: ClaimManagement, { payload }) => {
			draft.claimResponse = payload;
			draft.loading = LoadingStates.IDLE;
		});
		builder.addCase(rejectRetroClaim.rejected, (state, { payload }) => {
			state.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// Approve Retro Claim
		builder.addCase(approveRetroClaim.pending, (draft: ClaimManagement) => {
			draft.loading = LoadingStates.LOADING;
		});

		builder.addCase(approveRetroClaim.fulfilled, (draft: ClaimManagement) => {
			draft.loading = LoadingStates.IDLE;
		});
		builder.addCase(approveRetroClaim.rejected, (state, { payload }) => {
			state.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// Special Approval
		builder.addCase(specialApprovalRetroClaim.pending, (draft: ClaimManagement) => {
			draft.specialApproval.loading = LoadingStates.LOADING;
		});

		builder.addCase(specialApprovalRetroClaim.fulfilled, (draft: ClaimManagement, { payload }) => {
			draft.claimResponse = payload;
			draft.specialApproval.loading = LoadingStates.IDLE;
		});
		builder.addCase(specialApprovalRetroClaim.rejected, (state, { payload }) => {
			state.specialApproval.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// Get Retro Claim Permissions
		builder.addCase(getRetroClaimPermissions.pending, (state) => {
			state.loading = LoadingStates.LOADING;
		});

		builder.addCase(getRetroClaimPermissions.fulfilled, (state, { payload }) => {
			state.loading = LoadingStates.IDLE;
			state.claimPermissions = payload.data;
		});

		builder.addCase(getRetroClaimPermissions.rejected, (state, { payload }) => {
			state.loading = LoadingStates.IDLE;
			state.claimPermissions = null;
			setAuthTokenError(state, payload);
		});
	}
});

// Selectors
export const retroCreateClaimDetailsSelector = (state: RootState) => state?.claimManagement?.createClaim;
export const retroClaimIDSelector = (state: RootState) => state?.claimManagement?.claimResponse;
export const retroClaimListSelector = (state: RootState) => state?.claimManagement?.claimList;
export const getLoadingStateForClaimList = (state: RootState): LoadingStates => state?.claimManagement?.claimList.loading;
export const retroClaimDetailsSelector = (state: RootState) => state?.claimManagement?.retroClaim;
export const retroClaimSpecialApprovalLoadingState = (state: RootState) => state?.claimManagement?.specialApproval.loading;
export const getLoadingStateSelector = (state: RootState) => state?.claimManagement?.loading;
export const hasTokenErrRetroSelector = (state: RootState) => state?.claimManagement?.hasUnaurizedError;
export const getClaimPermissionsSelector = (state: RootState) => state?.claimManagement?.claimPermissions;

// Actions
export const { resetClaimState, createInfoUpdater, retroClaimInfoUpdater } = retroClaimSlice.actions;

// Reducers
export default retroClaimSlice.reducer;
